import type cytoscape from 'cytoscape'

const DISPLAY_NONE = 'none'
const DISPLAY_ELEMENT = 'element'

export type CyElement = cytoscape.NodeSingular | cytoscape.EdgeSingular

/**
 * Sets the visibility of a single element.
 */
export function setVisible(element: CyElement, visible: boolean): void {
  element.style('display', visible ? DISPLAY_ELEMENT : DISPLAY_NONE)
}

/**
 * Sets the visibility of a collection of elements.
 */
export function setCollectionVisible(
  elements: cytoscape.CollectionReturnValue,
  visible: boolean,
): void {
  elements.style('display', visible ? DISPLAY_ELEMENT : DISPLAY_NONE)
}

/**
 * Checks if an element is currently visible.
 */
export function isVisible(element: CyElement): boolean {
  return (element.style('display') as string) !== DISPLAY_NONE
}

/**
 * Checks if any child in the collection is visible.
 */
export function collectionHasVisibleChild(
  children: cytoscape.CollectionReturnValue,
): boolean {
  let hasVisibleChild = false
  children.forEach(child => {
    if (isVisible(child as cytoscape.NodeSingular)) {
      hasVisibleChild = true
    }
  })
  return hasVisibleChild
}

/**
 * Gets the direct children of a node.
 */
export function getChildren(
  node: cytoscape.NodeSingular,
): cytoscape.CollectionReturnValue {
  return node.outgoers('node')
}

/**
 * Computes the depth of each node from the root nodes using BFS.
 */
export function computeDepths(cy: cytoscape.Core): Map<string, number> {
  const depths = new Map<string, number>()
  const queue: cytoscape.NodeSingular[] = []

  cy.nodes()
    .filter(node => node.incomers('edge').length === 0)
    .forEach(root => {
      depths.set(root.id(), 0)
      queue.push(root)
    })

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) {
      continue
    }
    const depth = depths.get(current.id()) ?? 0

    current.outgoers('node').forEach(child => {
      const childNode = child as cytoscape.NodeSingular
      const nextDepth = depth + 1
      const existing = depths.get(childNode.id())
      if (existing === undefined || nextDepth < existing) {
        depths.set(childNode.id(), nextDepth)
        queue.push(childNode)
      }
    })
  }

  return depths
}

/**
 * Syncs edge visibility based on the visibility of connected nodes.
 */
export function syncEdgeVisibility(cy: cytoscape.Core): void {
  cy.edges().forEach(edge => {
    setVisible(edge, isVisible(edge.source()) && isVisible(edge.target()))
  })
}

/**
 * Updates the descendant count data attribute for all nodes.
 */
export function updateDescendantCounts(cy: cytoscape.Core): void {
  cy.nodes().forEach(node => {
    const descendantCount = node.successors('node').length
    node.data('descendantCount', descendantCount)
  })
}

/**
 * Updates the collapsed/expanded classes and data flags for all nodes.
 */
export function updateCollapsedFlags(cy: cytoscape.Core): void {
  cy.nodes().forEach(node => {
    const children = getChildren(node)
    const hasChildren = children.length > 0
    if (!hasChildren) {
      node.data('collapsed', false)
      node.removeClass('has-children')
      node.removeClass('collapsed')
      node.removeClass('expanded')
      return
    }
    node.addClass('has-children')
    const anyVisibleChild = collectionHasVisibleChild(children)
    const isCollapsed = !anyVisibleChild
    node.data('collapsed', isCollapsed)
    if (isCollapsed) {
      node.addClass('collapsed')
      node.removeClass('expanded')
    } else {
      node.addClass('expanded')
      node.removeClass('collapsed')
    }
  })
}

/**
 * Refreshes all expand/collapse visual indicators after visibility changes.
 */
export function refreshExpandCollapseState(cy: cytoscape.Core): void {
  syncEdgeVisibility(cy)
  updateDescendantCounts(cy)
  updateCollapsedFlags(cy)
}
